<template>
  <ValidationObserver v-slot="{ invalid }" ref="fileFormValidationObserver">
    <b-modal 
      v-model="modalOpen" 
      :title="computedModalTitle" 
      no-close-on-backdrop 
      no-close-on-esc 
      hide-header-close 
      scrollable
      @hidden="onModalHidden"
    >
      <div class="p-3 mt-4">
        <h5 class="mb-3">{{ computedUploadTitle }}&nbsp;<span v-if="processing"><b-spinner class="small mb-1" small label="Small Spinner"></b-spinner></span></h5>
        <div class="form">
          <b-form enctype="multipart/form-data" ref="fileForm" @submit.prevent="handleUpload">
            <div class="form-group row">
              <div class="col-12">
                <ValidationProvider name="File Type" :rules="`required|ext:${acceptedExtensions}`" v-slot="validation" >
                  <b-form-file
                    :id="fileInputId"
                    v-model="fileData.file"
                    :state="multiple ? (fileData.file && fileData.file.length > 0) : spr2_getValidationState(validation)"
                    :placeholder="filePlaceholder"
                    :drop-placeholder="dropPlaceholder"
                    @input="onFileSelectionChanged"
                    :accept="acceptedExtensionsWithDot"
                    :multiple="multiple"
                  ></b-form-file>
                  <b-form-invalid-feedback :id="`${fileInputId}-feedback`">
                    {{ validation.errors[0] }}
                  </b-form-invalid-feedback>
                </ValidationProvider>
              </div>
            </div>
            
            <!-- Document Type Selection (only for documents) -->
            <div class="form-group row" v-if="showDocumentType && documentTypesOptions.length > 0">
              <div class="col-12 mt-md-0 mt-1">
                <ValidationProvider name="Document Type" rules="required" v-slot="validation" >
                  <b-form-select 
                    :state="spr2_getValidationState(validation)" 
                    :options="documentTypesOptions" 
                    v-model="fileData.documentTypeId" 
                    :aria-describedby="`${fileInputId}-type-feedback`"
                  >
                    <template #first>
                      <b-form-select-option :value="null" disabled>Type</b-form-select-option>
                    </template>
                  </b-form-select>
                  <b-form-invalid-feedback :id="`${fileInputId}-type-feedback`">{{ validation.errors[0] }}</b-form-invalid-feedback>
                </ValidationProvider>
              </div>
            </div>
            
            <!-- Name and Description fields (only for single file uploads) -->
            <div class="row mt-md-2" v-if="!multiple">
              <div class="col-lg-6 col-md-6 col-12 mt-md-0 mt-1">
                <ValidationProvider name="File Name" rules="required" v-slot="validation" >
                                      <b-form-input
                      class="w-100"
                      :placeholder="computedNamePlaceholder"
                      v-model="fileData.name"
                      :state="spr2_getValidationState(validation)"
                      :aria-describedby="`${fileInputId}-name-feedback`"
                    />
                  <b-form-invalid-feedback :id="`${fileInputId}-name-feedback`">{{ validation.errors[0] }}</b-form-invalid-feedback>
                </ValidationProvider>
              </div>
                              <div class="col-lg-6 col-md-6 col-12 mt-md-0 mt-1">
                  <b-form-input 
                    class="w-100" 
                    :placeholder="computedDescriptionPlaceholder" 
                    v-model="fileData.note" 
                  />
                </div>
            </div>
          </b-form>
        </div>
      </div>
      
      <template v-slot:modal-footer>
        <b-btn variant="light" @click="closeModal">Close</b-btn>
        <b-btn 
          type="submit" 
          variant="primary" 
          :disabled="!readyForUpload(invalid)" 
          @click="handleUpload" 
          class="float-right ml-1"
        >
          {{ saveButtonText || 'Save' }}
        </b-btn>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { spr2Validation } from '../mixins/Spr2SharedFunctions'

export default {
  name: 'FileUploadModal',
  mixins: [spr2Validation],
  props: {
    // Modal visibility
    value: {
      type: Boolean,
      default: false
    },
    // File type configuration
    fileType: {
      type: String,
      required: true,
      validator: value => ['document', 'image', 'video'].includes(value)
    },
    // Accepted file extensions (array of objects with ext, type, class, color properties)
    acceptedFileTypes: {
      type: Array,
      required: true
    },
    // Document types options (for document uploads)
    documentTypesOptions: {
      type: Array,
      default: () => []
    },
    // Processing state
    processing: {
      type: Boolean,
      default: false
    },
    // Event ID (optional)
    eventId: {
      type: [String, Number],
      default: null
    },
    // Folder information (optional)
    currentFolder: {
      type: Object,
      default: null
    },
    // Allow multiple files
    multiple: {
      type: Boolean,
      default: false
    },
    // Custom labels
    modalTitle: {
      type: String,
      default: null
    },
    uploadTitle: {
      type: String,
      default: null
    },
    namePlaceholder: {
      type: String,
      default: null
    },
    descriptionPlaceholder: {
      type: String,
      default: null
    },
    saveButtonText: {
      type: String,
      default: 'Save'
    }
  },
  data() {
    return {
      fileData: {
        id: null,
        name: null,
        file: null,
        fileName: null,
        documentTypeId: null,
        folderId: null,
        note: null
      }
    }
  },
  computed: {
    modalOpen: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    // Dynamic labels based on file type
    computedModalTitle() {
      return this.modalTitle || `Upload ${this.fileType.charAt(0).toUpperCase() + this.fileType.slice(1)}${this.multiple ? 's' : ''}`
    },
    computedUploadTitle() {
      return this.uploadTitle || `Upload New ${this.fileType.charAt(0).toUpperCase() + this.fileType.slice(1)}${this.multiple ? 's' : ''}`
    },
    computedNamePlaceholder() {
      return this.namePlaceholder || `${this.fileType.charAt(0).toUpperCase() + this.fileType.slice(1)} Name`
    },
    computedDescriptionPlaceholder() {
      return this.descriptionPlaceholder || 'Description'
    },
    // File input ID
    fileInputId() {
      return `${this.fileType}Upload`
    },
    // File placeholders
    filePlaceholder() {
      if (this.multiple) {
        return `Choose ${this.fileType}s or drop them here...`
      }
      return `Choose a ${this.fileType} or drop it here...`
    },
    dropPlaceholder() {
      if (this.multiple) {
        return `Drop ${this.fileType}s here...`
      }
      return `Drop ${this.fileType} here...`
    },
    // Accepted extensions
    acceptedExtensions() {
      return this.acceptedFileTypes.map(a => a.ext).join(',')
    },
    acceptedExtensionsWithDot() {
      return this.acceptedFileTypes.map(a => `.${a.ext}`).join(',')
    },
    // Show document type selector only for documents
    showDocumentType() {
      return this.fileType === 'document' && this.eventId && this.documentTypesOptions.length > 0
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.initializeFileData()
      }
    }
  },
  methods: {
    // Initialize file data
    initializeFileData(fileId = null) {
      if (fileId !== null) {
        // Edit mode - populate with existing data
        this.fileData = {
          id: fileId,
          name: null,
          file: null,
          fileName: null,
          documentTypeId: null,
          folderId: null,
          note: null
        }
      } else {
        // New upload mode
        this.fileData = {
          id: null,
          name: null,
          file: null,
          fileName: null,
          documentTypeId: null,
          folderId: null,
          note: null
        }
      }
    },
    
    // Handle file selection change
    onFileSelectionChanged(file) {
      if (file !== null) {
        if (this.multiple) {
          // For multiple files, don't set individual names
          this.fileData.file = file
        } else {
          // For single file, set name and fileName
          this.fileData.name = file.name
          this.fileData.fileName = file.name
          this.fileData.file = file
        }
      }
    },
    
    // Check if ready for upload
    readyForUpload(invalid) {
      if (this.multiple) {
        // For multiple files, just check if files are selected and not processing
        return (this.fileData.file && this.fileData.file.length > 0 && this.processing === false)
      } else {
        // For single file, check validation and processing
        return (invalid === false && this.processing === false)
      }
    },
    
    // Handle upload submission
    handleUpload() {
      // Prepare file data
      const uploadData = { ...this.fileData }
      
      // Add folder information if available
      if (this.currentFolder !== null) {
        uploadData.folderId = this.currentFolder.id
        uploadData.folderType = this.currentFolder.folderType
        uploadData.propertyId = this.currentFolder.propertyId
      }
      
      // Add event ID if available
      if (this.eventId) {
        uploadData.eventId = this.eventId
      }
      
      // Emit upload event
      this.$emit('upload', uploadData)
    },
    
    // Close modal
    closeModal() {
      this.modalOpen = false
    },
    
    // Handle modal hidden event
    onModalHidden() {
      this.$emit('modal-hidden')
    },
    
    // Public method to reset form
    resetForm() {
      this.initializeFileData()
      if (this.$refs.fileFormValidationObserver) {
        this.$refs.fileFormValidationObserver.reset()
      }
    }
  }
}
</script>

<style scoped>
/* Component-specific styles can be added here */
</style>
